import { lazy, Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { getPersistor, getStore } from 'redux/reemaStore.util';
import { DialogProvider } from 'utilities/dialog-context/DialogContext';
import { DialogProvider as DialogProviderV2 } from '../../stories/dialogs/DialogProvider';
import { PersistGate } from 'redux-persist/integration/react';
import { useGetAppConfig } from 'utilities/appConfigHook.hook';
import AppConfigLoadError from './AppConfigLoadError';
import InitialDataHelper from './InitialDataHelper';
import { LanguageProvider } from 'utilities/language-provider';
import { SideMenuDrawerProvider } from '@act/utilities/SideMenuDrawerContext';
import { ReemaThemeV2 } from '@act/storybook/theme/reemaThemeV2';
import { SettingsProvider } from '@act/storybook/theme/settings-drawer/SettingsProvider';
import { DisplayModeProvider } from '@act/storybook/theme/display/DisplayModeProvider';
import { DrawerProvider } from '@act/storybook/containers/Drawer/DrawerProvider';
import { RecapProvider } from '@act/storybook/theme/recap-drawer/RecapProvider';
import { Outlet } from 'react-router-dom';
import { InteractionsDialogRoot } from 'react-components/dialog-forms/hooks/useInteractionsDialog.hook';
import ToastPortal from 'react-components/portals/ToastPortal';
import { TrackingProvider } from '../tracking/TrackingContext';
import { JustInTimeCallDisplay } from '../calls/JustInTimeCallDisplay';
import { MinimizedCallDisplay } from '../calls/MinimizedCallDisplay';
import { GuidesRecapReadyToast } from '../member-page/Calls/PostCall/GuidesRecapReadyToast';

const RecapDrawer = lazy(() =>
  import('@act/storybook/theme/recap-drawer/RecapDrawer').then(module => ({
    default: module.RecapDrawer,
  }))
);

const CareAppContent = () => {
  const appConfig = useGetAppConfig();
  if (appConfig.status === 'loading') {
    return null;
  }
  if (appConfig.status === 'error') {
    return <AppConfigLoadError message={appConfig.errorMessage} />;
  }

  return (
    <Provider store={getStore()}>
      <PersistGate persistor={getPersistor()}>
        <LanguageProvider>
          <InitialDataHelper />
          <SettingsProvider>
            <ThemeProvider theme={ReemaThemeV2.LIGHT_MODE}>
              <DrawerProvider>
                <DisplayModeProvider>
                  <RecapProvider>
                    <DialogProviderV2>
                      <DialogProvider>
                        <SideMenuDrawerProvider>
                          <Outlet />
                          <InteractionsDialogRoot />
                          <ToastPortal />
                          <GuidesRecapReadyToast />
                          <JustInTimeCallDisplay />
                          <MinimizedCallDisplay />
                          <Suspense fallback={null}>
                            <RecapDrawer />
                          </Suspense>
                        </SideMenuDrawerProvider>
                      </DialogProvider>
                    </DialogProviderV2>
                  </RecapProvider>
                </DisplayModeProvider>
                <div id="toast-portal"></div>
              </DrawerProvider>
            </ThemeProvider>
          </SettingsProvider>
        </LanguageProvider>
      </PersistGate>
    </Provider>
  );
};

/**
 * Main Care Portal shell with an app-wide tracking context.
 * @returns Care Portal application root.
 */
const CareApp = () => (
  <TrackingProvider contextName="care-app">
    <CareAppContent />
  </TrackingProvider>
);

export default CareApp;
